import { createAsyncThunk } from "@reduxjs/toolkit";
import * as abacApi from "api/abac.api";
import abacCache from "utils/abacCache";
import { toPermittedActionList } from "./permittedActions";

/**
 * Load user capabilities (all permitted actions + feature flags)
 * This is the most efficient way to load permissions for UI gating
 * @param {string} [resourceTypes] - Optional comma-separated list of resource types to filter
 */
export const loadCapabilities = createAsyncThunk(
  "abac/loadCapabilities",
  async (resourceTypes = null, { rejectWithValue }) => {
    try {
      const response = await abacApi.getCapabilities(resourceTypes);
      return response.data || response;
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error?.message || "Failed to load capabilities");
    }
  }
);

/**
 * Load user authorization context
 * Fetches current user's roles, attributes, and permissions
 */
export const loadUserContext = createAsyncThunk(
  "abac/loadUserContext",
  async (_, { rejectWithValue }) => {
    try {
      const response = await abacApi.getUserContext();
      return response.data || response;
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error?.message || "Failed to load user context");
    }
  }
);

/**
 * Evaluate a single permission
 * @param {Object} params - Permission parameters
 * @param {string} params.resourceType - Type of resource
 * @param {string} params.action - Action to perform
 * @param {string} [params.resourceId] - Optional specific resource ID
 * @param {Object} [params.context] - Optional additional context
 */
export const evaluatePermission = createAsyncThunk(
  "abac/evaluatePermission",
  async ({ resourceType, action, resourceId, context }, { rejectWithValue }) => {
    try {
      // Generate cache key
      const cacheKey = abacCache.constructor.generateKey(
        resourceType,
        action,
        resourceId
      );

      // Check cache first
      const cachedResult = abacCache.get(cacheKey);
      if (cachedResult !== null) {
        return {
          result: cachedResult,
          cacheKey,
          fromCache: true,
        };
      }

      // Make API call
      const result = await abacApi.evaluatePermission({
        resourceType,
        action,
        resourceId,
        context,
      });

      return {
        result,
        cacheKey,
        fromCache: false,
      };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(
        error?.message || "Failed to evaluate permission"
      );
    }
  }
);

/**
 * Evaluate multiple permissions in a batch
 * @param {Array} permissions - Array of permission objects
 * @param {string} permissions[].resourceType - Type of resource
 * @param {string} permissions[].action - Action to perform
 * @param {string} [permissions[].resourceId] - Optional specific resource ID
 */
export const evaluateBatch = createAsyncThunk(
  "abac/evaluateBatch",
  async (permissions, { rejectWithValue }) => {
    try {
      if (!Array.isArray(permissions) || permissions.length === 0) {
        throw new Error("Permissions must be a non-empty array");
      }

      const results = await abacApi.evaluateBatch(permissions);

      return {
        results,
        permissions,
      };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(
        error?.message || "Failed to evaluate permissions batch"
      );
    }
  }
);

/**
 * Get all permitted actions for a resource type
 * @param {string} resourceType - Type of resource
 */
export const getPermittedActions = createAsyncThunk(
  "abac/getPermittedActions",
  async (resourceType, { rejectWithValue }) => {
    try {
      if (!resourceType) {
        throw new Error("Resource type is required");
      }

      const response = await abacApi.getPermittedActions(resourceType);
      const actions = toPermittedActionList(response);

      return {
        resourceType,
        actions,
      };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(
        error?.message || "Failed to get permitted actions"
      );
    }
  }
);
